import { createContext, useContext, useState, useEffect, useCallback } from 'react'
import api, { isAccessTokenExpired, refreshAccessToken } from '../services/api'
import websocketService from '../services/websocket'
import { useToast } from './ToastContext'

const AuthContext = createContext(null)

export function AuthProvider({ children }) {
    const [isAuthenticated, setIsAuthenticated] = useState(false)
    const [loading, setLoading] = useState(true)
    const [currentUser, setCurrentUser] = useState(null)
    const [demoModeEnabled, setDemoModeEnabled] = useState(false)
    const toast = useToast()

    const checkDemoMode = useCallback(async () => {
        try {
            const response = await api.get('/auth/demo-status')
            setDemoModeEnabled(response.data.demo_mode || false)
        } catch (error) {
            console.error('Failed to check demo mode status:', error)
            setDemoModeEnabled(false)
        }
    }, [])

    const checkAuth = useCallback(async () => {
        const token = localStorage.getItem('access_token')
        if (token) {
            // The access token is short-lived by design; the long-lived session lives in
            // the httpOnly refresh cookie. Renew proactively here so that a player who
            // returns after the TTL gets a valid token BEFORE the bootstrap calls run,
            // instead of bouncing to the login screen on the first 401.
            let activeToken = token
            if (isAccessTokenExpired(activeToken)) {
                try {
                    activeToken = await refreshAccessToken()
                } catch (refreshError) {
                    console.warn('Session renewal on bootstrap failed:', {
                        message: refreshError.response?.data?.detail || refreshError.message,
                        status: refreshError.response?.status
                    })
                    activeToken = null
                }
            }

            if (!activeToken) {
                localStorage.removeItem('access_token')
                setIsAuthenticated(false)
                setCurrentUser(null)
                setLoading(false)
                return
            }

            try {
                const [authRes] = await Promise.all([
                    api.get('/auth/me')
                ])

                // Robust player state fetching with retries
                let playerRes = null;
                const maxRetries = 3;
                for (let i = 0; i < maxRetries; i++) {
                    try {
                        playerRes = await api.get('/player/state');
                        break; // Success
                    } catch (err) {
                        // If player doesn't exist (404), it's not a failure, 
                        // just means they haven't set a handle yet.
                        if (err.response?.status === 404) {
                            playerRes = null;
                            break;
                        }
                        
                        console.warn(`Attempt ${i + 1}/${maxRetries} to fetch player state failed.`);
                        if (i === maxRetries - 1) throw err; // Throw on last attempt (real error)
                        await new Promise(r => setTimeout(r, 500 * (i + 1))); // Backoff
                    }
                }

                setIsAuthenticated(true)

                // Merge player handle into user data
                const userData = {
                    ...authRes.data,
                    handle: playerRes?.data?.handle
                }

                // Check for daily rewards // TODO: Move to a separate hook or service
                if (playerRes?.data?.daily_reward_available) {
                    const lastToastTime = localStorage.getItem('last_daily_reward_toast_time');
                    const now = Date.now();
                    const fifteenMinutes = 15 * 60 * 1000;

                    if (!lastToastTime || (now - parseInt(lastToastTime)) > fifteenMinutes) {
                        toast.dailyReward("A new Daily Reward is available! Check the User Menu.");
                        localStorage.setItem('last_daily_reward_toast_time', now.toString());
                    }
                }

                setCurrentUser(userData)
            } catch (error) {
                const errorMessage = error.response?.data?.detail || error.message || 'Unknown error'
                const errorStatus = error.response?.status
                console.error('Authentication check failed:', {
                    message: errorMessage,
                    status: errorStatus,
                    error: error
                })
                localStorage.removeItem('access_token')
                setIsAuthenticated(false)
                setCurrentUser(null)
            }
        } else {
            setIsAuthenticated(false)
            setCurrentUser(null)
        }
        setLoading(false)
    }, [toast])

    const login = useCallback((provider) => {
        const loginUrl = `/api/auth/login/${provider}`
        console.log(`Attempting login with provider: ${provider}`, loginUrl)
        window.location.href = loginUrl
    }, [])

    const localLogin = async (email, password) => {
        try {
            const response = await api.post('/auth/login', { email, password })
            const { access_token } = response.data
            localStorage.setItem('access_token', access_token)
            await checkAuth()
            return response.data
        } catch (error) {
            throw error
        }
    }

    const register = async (email, password, turnstileToken) => {
        try {
            const response = await api.post('/auth/register', { 
                email, 
                password, 
                turnstile_token: turnstileToken 
            })
            return response.data
        } catch (error) {
            throw error
        }
    }

    const verifyEmail = async (token) => {
        try {
            const response = await api.post('/auth/verify', { token })
            const { access_token } = response.data
            if (access_token) {
                localStorage.setItem('access_token', access_token)
                await checkAuth()
            }
            return response.data
        } catch (error) {
            throw error
        }
    }

    const forgotPassword = async (email, turnstileToken) => {
        try {
            const response = await api.post('/auth/forgot-password', { 
                email, 
                turnstile_token: turnstileToken 
            })
            return response.data
        } catch (error) {
            throw error
        }
    }

    const resetPassword = async (token, new_password, turnstileToken) => {
        try {
            const response = await api.post('/auth/reset-password', { 
                token, 
                new_password,
                turnstile_token: turnstileToken
            })
            return response.data
        } catch (error) {
            throw error
        }
    }

    const logout = useCallback(() => {
        localStorage.removeItem('access_token')
        setIsAuthenticated(false)
        setCurrentUser(null)
        websocketService.disconnect()
    }, [])

    // Initial Auth Check
    useEffect(() => {
        // We can't easily check for 'auth/callback' route here without router location, 
        // but the callback component will handle the token set and then we'll likely reload or re-mount.
        // For now, simple checkAuth on mount.
        checkAuth()
        checkDemoMode()
    }, [checkAuth, checkDemoMode])


    // WebSocket Connection Management
    useEffect(() => {
        if (isAuthenticated) {
            const token = localStorage.getItem('access_token')
            if (token) {
                websocketService.connect(token)
            }
        } else {
            websocketService.disconnect()
        }
        // Cleanup on unmount
        return () => {
            websocketService.disconnect()
        }
    }, [isAuthenticated])


    const value = {
        isAuthenticated,
        loading,
        currentUser,
        demoModeEnabled,
        login,
        localLogin,
        register,
        verifyEmail,
        forgotPassword,
        resetPassword,
        logout,
        checkAuth, // Exposed so AuthCallback can trigger it
        setIsAuthenticated // Exposed for AuthCallback
    }

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    )
}

export function useAuth() {
    const context = useContext(AuthContext)
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider')
    }
    return context
}
